import { Box, Button, Divider, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import BookOutlinedIcon from "@mui/icons-material/BookOutlined";
import { useUpdatePrimaryBusinessBox } from "../../../../services/mutations";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import AddDetailsForm from "./AddDetailsForm";
import AddLinksBusinessBoxForm from "./AddLinksBusinessBoxForm";
import CTAForm from "./CTAForm";
import { useFormikContext } from "formik";
import { ButtonLoader } from "../../../inputs.styled";
import BusinessBoxManager from "./ManageBusinessBox";
import { colors } from "../../../../utils";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { updateProfileBusinessBox } from "../../../../redux/reducers/gallery";
import {
  pushToHistoryAction,
  setBusinessBoxWheel,
} from "../../../../redux/reducers/segmentBuilder";
import { editUserProfileAction } from "../../../../redux/actions";
import Tabs from "./Tabs";

const tabsList = [
  {
    label: "Content",
    value: "0",
  },
  {
    label: "Icon Links",
    value: "1",
  },
  {
    label: "Button Links",
    value: "2",
  },
];

const InnerForm = ({
  bBox,
  bBoxSetter,
  onClose,
  apiLoading,
  apiLoadingSetter,
  isWheelBusinessBox,
  currentBBox,
  openLibrarySection = false, // Add this prop
  handleBusinessBoxPermissions,
}) => {
  const { handleSubmit, isValid, setValues, resetForm, errors } =
    useFormikContext();
  const [showErrors, setShowErrors] = useState(false);
  const [selectedTab, setSelectedTab] = useState("0");
  const [isManagerOpen, setIsManagerOpen] = useState(openLibrarySection); // Set initial value based on prop
  const [errorMessage, setErrorMessage] = useState("");

  // Function to determine which tabs have errors
  const getTabErrors = () => {
    const tabErrors = {};

    // Tab 0 (Content): business_title, description, media_url
    if (errors.business_title || errors.description || errors.media_url) {
      tabErrors["0"] = true;
    }

    // Tab 1 (Icon Links): business_link
    if (errors.business_link) {
      tabErrors["1"] = true;
    }

    // Tab 2 (Button Links): business_box_cta
    if (errors.business_box_cta) {
      tabErrors["2"] = true;
    }

    return tabErrors;
  };

  const dispatch = useDispatch();

  useEffect(() => {
    if (isValid) {
      setShowErrors(false);
      setErrorMessage("");
    }
  }, [isValid]);

  //if the edit mode is set to true, close the manager
  useEffect(() => {
    if (bBox && bBox.isEditMode) {
      //set the formik values also
      setValues(bBox);
      if (bBox.closeManager) {
        setIsManagerOpen(false);
      }

      //if the manager is closed, reset the manager bbox preview or else
      //it will conflict with the opened form.
      //bBoxSetter(null);
    }
    // else {
    //   resetForm();
    // }
  }, [bBox]);

  // This effect will open the library section on mount if the prop is true
  useEffect(() => {
    if (openLibrarySection) {
      setIsManagerOpen(true);
    }
  }, [openLibrarySection]);

  const renderFormContent = () => {
    if (isManagerOpen) {
      return (
        <BusinessBoxManager
          bBox={bBox}
          setter={bBoxSetter}
          closeManager={() => setIsManagerOpen(false)}
          isWheelBusinessBox={isWheelBusinessBox}
          currentBBox={currentBBox}
          handleBusinessBoxPermissions={handleBusinessBoxPermissions}
        />
      );
    }
    switch (selectedTab) {
      case "0":
        return (
          <AddDetailsForm
            isWheelBusinessBox={isWheelBusinessBox}
            showErrors={showErrors}
            bBoxSetter={bBoxSetter}
          />
        );
      case "1":
        return <AddLinksBusinessBoxForm showErrors={showErrors} />;
      case "2":
        return <CTAForm showErrors={showErrors} />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        pr: "20px",
        borderRight: "1px solid #E1E1E1",
        height: "100%", // Add full height
        display: "flex",
        flexDirection: "column", // Use flex layout
      }}
    >
      {/* Back button - only show when manager is open */}
      {isManagerOpen && (
        <Box
          sx={{
            pt: 1,
            pb: 0.5,
            position: "sticky",
            top: 0,
            zIndex: 6,
            backgroundColor: colors.white,
          }}
        >
          <Button
            onClick={() => setIsManagerOpen(false)}
            startIcon={<ArrowBackIcon />}
            sx={{
              color: "white",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: "500",
              padding: "4px 8px",
              borderRadius: "4px",
              border: "1px solid #E1E1E1",
              background: "#9B5AF8",
              "&:hover": {
                backgroundColor: "rgba(107, 31, 184, 0.04)",
                color: "#0F0F0F",
                borderColor: "#9B5AF8",
              },
            }}
          >
            Back to Business Box
          </Button>
        </Box>
      )}

      {/* Header Section */}
      <Box
        sx={{
          py: "1rem",
          position: "sticky",
          top: isManagerOpen ? 48 : -12, // Adjust sticky position when back button is visible
          zIndex: 5,
          backgroundColor: colors.white,
        }}
      >
        <Box
          sx={{
            backgroundColor: colors.white,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: "#0F0F0F",
              fontSize: "20px",
              fontWeight: "700",
              lineHeight: "20px",
              fontFamily: "DM Sans",
            }}
          >
            {isManagerOpen
              ? "Business Box Library"
              : bBox && bBox.isEditMode
              ? "Edit Business Box"
              : "Business Box"}
          </Typography>

          {!isManagerOpen && (
            <Button
              onClick={() => setIsManagerOpen(true)}
              sx={{
                color: "#565556",
                fontSize: "16px",
                fontWeight: "500",
                borderRadius: "4px",
                background: "#F6F7F9",
                textTransform: "capitalize",
                padding: "6px 12px",
              }}
              startIcon={<BookOutlinedIcon sx={{ color: "#9B5AF8" }} />}
            >
              Library
            </Button>
          )}
        </Box>
      </Box>

      {/* Content Section */}
      <Box
        sx={{
          width: "100%",
          flex: 1, // Take remaining space
          display: "flex",
          flexDirection: "column",
          minHeight: 0, // Allow content to scroll
          overflow: "hidden", // Changed from "auto" to "hidden"
        }}
      >
        {!isManagerOpen && (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 5,
              backgroundColor: colors.white,
            }}
          >
            <Tabs
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              tabList={tabsList}
              orientation="horizontal"
              borderTop
              tabErrors={getTabErrors()}
            />
          </Box>
        )}

        <Box
          sx={{
            overflow: "auto", // Enable scrolling for content
            flex: 1, // Take remaining space
          }}
        >
          {renderFormContent()}
        </Box>
      </Box>

      {/* Footer Section */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: colors.white,
          p: 3,
          zIndex: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.2,
          borderTop: "2px solid #BBBBBB",
          mt: "auto", // Push to bottom
        }}
      >
        <Button
          sx={{
            color: "#495057",
            textTransform: "capitalize",
            fontSize: "16px",
            fontWeight: "500",
          }}
          onClick={onClose}
        >
          Cancel
        </Button>

        <Button
          onClick={() => {
            if (!isValid) {
              if (!bBox || bBox.isEditMode) {
                setShowErrors(true);
                setErrorMessage("Some fields are missing or invalid!");

                setTimeout(() => {
                  setErrorMessage("");
                }, 4000);
                return;
              }
            }
            //if the manager was open, when save button was clicked,
            //it is likely a call to set the PrimaryBox
            if (isManagerOpen) {
              if (bBox.id) {
                // applyBusinessBox(bBox.id);
                handleBusinessBoxPermissions();
              } else
                console.warn(
                  "An attempt was made to set the primary box without any id"
                );
            } else handleSubmit();
          }}
          sx={{
            color: "white",
            textTransform: "capitalize",
            fontSize: "16px",
            fontWeight: "500",
            background: "#9B5AF8",

            "&:hover": {
              background: "#9B5AF885",
            },
          }}
        >
          {apiLoading && <ButtonLoader />} {isManagerOpen ? "Select" : "Save"}
        </Button>

        {errorMessage && (
          <div
            style={{
              color: "red",
              marginLeft: "10px",
              backgroundColor: "wheat",
              borderRadius: "8px",
              fontSize: "12px",
              position: "absolute",
              top: "-30px",
              right: "20px",
              padding: "8px 16px",
            }}
          >
            {errorMessage}
          </div>
        )}
      </Box>
    </Box>
  );
};

export default InnerForm;
